/* import __COLOCATED_TEMPLATE__ from './filter.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';

export enum Operator {
  Equal = '=',
  NotEqual = '!=',
  GreaterThan = '>',
  LessThan = '<',
  GreaterThanOrEqual = '>=',
  LessThanOrEqual = '<=',
  Like = 'LIKE',
  NotLike = 'NOT LIKE',
}

export type DataType = 'string' | 'number' | 'boolean' | 'datetime';

export interface Predicate {
  name: string;
  type: DataType;
  operator: Operator;
  value: string | number | boolean;
}

export interface Filter {
  icon: InterfaceIconName;
  isDisabled: boolean;
  name: string;
  text: string;
  predicate: Predicate;
  type: string;
}

interface FilterItem {
  filter: Filter;
  index: number;
  component:
    | 'standalone/salesforce/setup/article-sync/filter-items/boolean-filter'
    | 'standalone/salesforce/setup/article-sync/filter-items/number-filter'
    | 'standalone/salesforce/setup/article-sync/filter-items/date-filter'
    | 'standalone/salesforce/setup/article-sync/filter-items/string-filter';
}

export type ArticleImportMode = 'all' | 'filtered';

// Salesforce does not allow COUNT when these fields are used in the WHERE clause
const RESTRICTED_FILTER_TYPES = ['id', 'reference'];
const RESTRICTED_FILTER_FIELDS = ['VersionNumber'];

// eslint-disable-next-line @intercom/intercom/no-bare-strings
const STANDARD_FILTERS_FIELDS = ['ArticleType', 'CreatedDate', 'IsDeleted', 'Language'];

interface Args {
  nextStep: () => void;
  previousStep: () => void;
  sourceId?: number;
  importSource?: ImportSource;
  setImportSource?: (importSource: ImportSource) => void;
  toggleSideDrawer: () => void;
}

export default class StandaloneSalesforceArticleSyncStepFilter extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;

  @tracked articleImportMode: ArticleImportMode = 'all';
  @tracked selectedFilters: Filter[] = [];
  @tracked importSource?: ImportSource;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.initialize();
  }

  private async initialize() {
    await taskFor(this.loadImportSourcesTask).perform();
  }

  get isLoadingImportSource() {
    return taskFor(this.loadImportSourcesTask).isRunning;
  }

  get isUpdatingPredicates() {
    return taskFor(this.updatePredicates).isRunning;
  }

  get showLoading() {
    return this.isLoadingImportSource || this.isUpdatingPredicates;
  }

  get showFilterSelection() {
    return this.articleImportMode === 'filtered';
  }

  get salesforceContentCount() {
    return this.importSource?.remoteContentCount || 0;
  }

  get filterItems(): FilterItem[] {
    return this.selectedFilters.map((filter, index) => ({
      filter,
      index,
      component: this.getComponentForFilter(filter),
    }));
  }

  @action removeFilter(index: number) {
    this.selectedFilters = this.selectedFilters.filter((_, i) => i !== index);
    taskFor(this.updatePredicates).perform();
  }

  @action
  addFilter(filter: Filter) {
    this.selectedFilters = [...this.selectedFilters, filter];
    taskFor(this.updatePredicates).perform();
  }

  @action
  setPredicate(predicate: Predicate, index: number) {
    let filters = [...this.selectedFilters];
    filters[index] = {
      ...filters[index],
      predicate,
    };

    this.selectedFilters = filters;
    taskFor(this.updatePredicates).perform();
  }

  @action
  setArticleImportMode(mode: ArticleImportMode) {
    this.articleImportMode = mode;
    if (mode === 'all') {
      this.selectedFilters = [];
    }
    taskFor(this.updatePredicates).perform();
  }

  get filterOptions() {
    return [
      {
        heading: this.intl.t(
          'standalone.salesforce.setup.article-sync.steps.filter.standard-fields',
        ),
        items: this.standardFilterOptions,
        isFilterable: true,
      },
      {
        heading: this.intl.t('standalone.salesforce.setup.article-sync.steps.filter.custom-fields'),
        items: this.customFilterOptions,
        isFilterable: true,
      },
    ];
  }

  get isHelperTextError() {
    return !this.showLoading && this.salesforceContentCount === 0;
  }

  get isContinueDisabled() {
    return this.showLoading || this.salesforceContentCount === 0;
  }

  get serializedFilters(): Filter[] {
    return (
      this.importSource?.metadata
        ?.filter(
          (item: any) =>
            item.filterable &&
            !RESTRICTED_FILTER_TYPES.includes(item.type) &&
            !RESTRICTED_FILTER_FIELDS.includes(item.name),
        )
        .map((item: any) => ({
          text: item.label,
          name: item.name,
          icon: this.filterIcon(item),
          type: item.type,
          predicate: this.filterToPredicate(item),
        })) || []
    );
  }

  get standardFilterOptions() {
    return this.serializedFilters
      .filter((item) => STANDARD_FILTERS_FIELDS.includes(item.name))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  get customFilterOptions() {
    return this.serializedFilters
      .filter((item) => !STANDARD_FILTERS_FIELDS.includes(item.name))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  private filterIcon(filter: Filter): InterfaceIconName {
    switch (filter.type) {
      case 'boolean':
      case 'bool':
        return 'boolean';
      case 'number':
      case 'int':
      case 'float':
        return 'numbers';
      case 'datetime':
      case 'date':
        return 'calendar';
      case 'picklist':
      case 'multipicklist':
        return 'list';
      default:
        return 'small-text';
    }
  }

  private getComponentForFilter(filter: Filter) {
    switch (filter.type) {
      case 'boolean':
      case 'bool':
        return 'standalone/salesforce/setup/article-sync/filter-items/boolean-filter';
      case 'number':
      case 'int':
      case 'float':
        return 'standalone/salesforce/setup/article-sync/filter-items/number-filter';
      case 'datetime':
      case 'date':
        return 'standalone/salesforce/setup/article-sync/filter-items/date-filter';
      default:
        return 'standalone/salesforce/setup/article-sync/filter-items/string-filter';
    }
  }

  private filterToPredicate(filter: any): Predicate {
    switch (filter.type) {
      case 'boolean':
      case 'bool':
        return {
          name: filter.name,
          type: 'boolean',
          operator: Operator.Equal,
          value: true,
        };
      case 'number':
      case 'int':
      case 'float':
        return {
          name: filter.name,
          type: 'number',
          operator: Operator.Equal,
          value: 0,
        };
      case 'datetime':
      case 'date':
        return {
          name: filter.name,
          type: 'datetime',
          operator: Operator.Equal,
          value: new Date().toISOString(),
        };
      default:
        return {
          name: filter.name,
          type: 'string',
          operator: Operator.Equal,
          value: '',
        };
    }
  }

  private hydrateFiltersFromSource() {
    let predicates = this.importSource?.context?.predicates as Predicate[] | undefined;

    if (predicates && predicates.length > 0) {
      let filters = predicates
        .map((p) => this.predicateToFilter(p))
        .filter((f): f is Filter => !!f);
      if (filters.length > 0) {
        this.selectedFilters = filters;
        this.articleImportMode = 'filtered';
      }
    }
  }

  private predicateToFilter(predicate: Predicate): Filter | undefined {
    let baseFilter = this.serializedFilters.find((f) => f.name === predicate.name);

    if (!baseFilter) {
      return;
    }

    return {
      ...baseFilter,
      predicate,
    };
  }

  @task({ drop: true })
  *updatePredicates(): TaskGenerator<void> {
    if (!this.importSource) {
      return;
    }
    let predicates = this.selectedFilters.map((filter) => filter.predicate);
    yield this.knowledgeHubService.updateImportSourceWithParam(this.importSource.id, {
      predicates,
    });
    this.importSource = yield this.importSource.reload();
  }

  @task({ drop: true })
  *loadImportSourcesTask(): TaskGenerator<void> {
    // Use the import source passed from step-wrapper if available
    if (this.args.importSource) {
      this.importSource = this.args.importSource;
      yield this.importSource.reload();
      this.hydrateFiltersFromSource();
      return;
    }

    // Otherwise, try to find by sourceId first
    if (this.args.sourceId) {
      this.importSource = yield this.store.findRecord(
        'import-service/import-source',
        Number(this.args.sourceId),
      );
    }

    if (this.importSource) {
      yield this.importSource.reload();
      this.hydrateFiltersFromSource();
    } else {
      let sources = yield this.knowledgeHubService.fetchImportSources();
      let sfSource = sources.find(
        (source: ImportSource) => source.sourceType === SourceType.Salesforce,
      );

      if (sfSource) {
        this.importSource = yield sfSource.reload();
      } else {
        try {
          this.importSource = yield this.knowledgeHubService.createImportSource({
            sourceType: SourceType.Salesforce,
          });
        } catch (error) {
          if (this.isSalesforceMetaDataError(error)) {
            this.notificationsService.notifyError(
              this.intl.t('standalone.salesforce.setup.article-sync.metadata-error'),
            );
          }
        }
      }
    }
  }

  private isSalesforceMetaDataError(error: any): boolean {
    let payload = error.jqXHR?.responseJSON;
    return (
      payload &&
      payload.error?.type === 'MetadataNotFoundError' &&
      payload.error?.source === 'salesforce'
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::Steps::Filter': typeof StandaloneSalesforceArticleSyncStepFilter;
    'standalone/salesforce/setup/article-sync/steps/filter': typeof StandaloneSalesforceArticleSyncStepFilter;
  }
}
